// "Working as Jesse · signs out in 7 min · Switch person (PIN) · Sign out" (Shipping hub, Phase 1).
// Shows this tab's operator session (features/operatorSession/session.js) and lets the person at the screen
// sign in, switch or sign out. The session itself ends on the server 10 minutes after sign-in.
import React, { useCallback, useEffect, useState } from 'react';
import { liveSession, minutesLeft, onSessionChange } from './session';
import { signIn, signOut, switchPerson, verifySession } from './workingAsUi';

const TICK_MS = 15 * 1000;

const styles = {
  chip: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
    padding: '6px 10px', borderRadius: 10,
    border: '1px solid var(--bs-border-color, #dee2e6)',
    background: 'var(--jq-bg-card, var(--bs-body-bg, #fff))',
  },
  who: { fontSize: '1.05rem', lineHeight: 1.2 },
  left: { fontSize: '0.9rem' },
  btn: { minHeight: 44, minWidth: 44 },
};

/** `ui`: anything with alert(message) (desktopUi). `deps`: test seam for workingAsUi's sign-in. */
export default function WorkingAsChip({ ui = null, deps = undefined }) {
  const [session, setSession] = useState(() => liveSession());
  const [, setTick] = useState(0);
  const [busy, setBusy] = useState(false);
  const alert = ui && ui.alert ? (m) => ui.alert(m) : null;
  const depOpt = deps ? { deps } : {};

  const refresh = useCallback(() => setSession(liveSession()), []);

  useEffect(() => onSessionChange(refresh), [refresh]);

  // the countdown, and a session that ran out leaves the chip
  useEffect(() => {
    const t = setInterval(() => { setTick((n) => n + 1); refresh(); }, TICK_MS);
    return () => clearInterval(t);
  }, [refresh]);

  // a stored session is checked once against JobQ (a PIN changed or a person switched off ends it there)
  useEffect(() => {
    let alive = true;
    verifySession(depOpt).then(() => { if (alive) refresh(); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function run(fn) {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
      refresh();
    }
  }

  if (!session) {
    return (
      <div style={styles.chip} data-testid="working-as-chip">
        <span className="text-muted" style={styles.who}>No one signed in</span>
        <button type="button" className="btn btn-primary" style={styles.btn} disabled={busy}
          onClick={() => run(() => signIn({ alert, ...depOpt }))}>
          Sign in (PIN)
        </button>
      </div>
    );
  }

  const mins = minutesLeft(session);
  return (
    <div style={styles.chip} data-testid="working-as-chip">
      <span style={styles.who}>Working as <strong>{session.name || 'someone'}</strong></span>
      <span className={mins <= 2 ? 'text-danger' : 'text-muted'} style={styles.left}>
        · signs out in {mins} min
      </span>
      <button type="button" className="btn btn-outline-primary" style={styles.btn} disabled={busy}
        onClick={() => run(() => switchPerson({ alert, ...depOpt }))}>
        Switch person (PIN)
      </button>
      <button type="button" className="btn btn-outline-secondary" style={styles.btn} disabled={busy}
        onClick={() => run(() => signOut(depOpt))}>
        Sign out
      </button>
    </div>
  );
}
